import React, { useState } from 'react';
import styled from 'styled-components';
import { Filter, Download, Info, AlertTriangle, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { useConflits } from '../hooks/useConflits';

const Container = styled.section`
  background: linear-gradient(180deg, var(--white) 0%, var(--background-light) 100%);
  padding: 5rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--border-light), transparent);
  }
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Subtitle = styled.span`
  display: inline-block;
  color: var(--secondary-red);
  font-weight: 700;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.75rem;
`;

const Title = styled.h2`
  font-family: var(--font-secondary);
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 1.875rem;
  }
`;

const Description = styled.p`
  color: var(--text-light);
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
`;

const MapWrapper = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  border: 1px solid var(--border-light);
`;

const MapToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-light);
  background: var(--off-white);
  flex-wrap: wrap;
  gap: 1rem;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 600;
  transition: var(--transition);
  background: ${props => props.$active ? props.$bg : 'var(--white)'};
  color: ${props => props.$active ? 'var(--white)' : 'var(--text-dark)'};
  border: 1px solid ${props => props.$active ? props.$bg : 'var(--border-light)'};

  &:hover {
    border-color: ${props => props.$bg};
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.$active ? 'var(--white)' : props.$dotColor};
  }
`;

const ToolbarActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 600;
  background: var(--white);
  color: var(--text-dark);
  border: 1px solid var(--border-light);
  transition: var(--transition);

  &:hover {
    background: var(--primary-blue);
    color: var(--white);
    border-color: var(--primary-blue);
  }
`;

const MapContainer = styled.div`
  position: relative;
  height: 500px;
  background: linear-gradient(135deg, #f0f4f8 0%, #e8eef3 100%);
  overflow: hidden;

  @media (max-width: 768px) {
    height: 400px;
  }
`;

const MapSVG = styled.svg`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const MapRegion = styled.path`
  fill: ${props => props.$highlighted ? 'rgba(0, 38, 100, 0.2)' : 'rgba(0, 38, 100, 0.08)'};
  stroke: var(--white);
  stroke-width: 2;
  transition: var(--transition);
  cursor: pointer;

  &:hover {
    fill: rgba(0, 38, 100, 0.25);
  }
`;

const MapRegionOutline = styled.path`
  fill: none;
  stroke: rgba(0, 38, 100, 0.3);
  stroke-width: 1;
`;

const RegionLabel = styled.text`
  font-size: 11px;
  font-weight: 600;
  fill: var(--text-medium);
  text-anchor: middle;
  pointer-events: none;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
`;

const MapMarkers = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Marker = styled.div`
  position: absolute;
  left: ${props => props.$x}%;
  top: ${props => props.$y}%;
  transform: translate(-50%, -50%);
  pointer-events: auto;
  cursor: pointer;

  .marker-dot {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${props => props.$color};
    border: 3px solid var(--white);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    transition: var(--transition);
    position: relative;

    &::after {
      content: '';
      position: absolute;
      inset: -6px;
      border-radius: 50%;
      border: 2px solid ${props => props.$color};
      opacity: 0.3;
      animation: pulse 2s infinite;
    }

    &:hover {
      transform: scale(1.2);
    }
  }

  .marker-tooltip {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(-8px);
    background: var(--white);
    padding: 0.75rem 1rem;
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    white-space: nowrap;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-dark);
    opacity: 0;
    visibility: hidden;
    transition: var(--transition);

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-top-color: var(--white);
    }
  }

  &:hover .marker-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const MapLegend = styled.div`
  position: absolute;
  bottom: 1.5rem;
  left: 1.5rem;
  background: var(--white);
  padding: 1rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  min-width: 180px;
`;

const LegendTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-medium);

  &:last-child {
    margin-bottom: 0;
  }
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.$color};
  border: 2px solid var(--white);
  box-shadow: 0 0 0 1px var(--border-light);
`;

const StatsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--border-light);

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatItem = styled.div`
  background: var(--white);
  padding: 1.25rem;
  text-align: center;
  transition: var(--transition);

  &:hover {
    background: var(--off-white);
  }

  .stat-value {
    font-size: 1.5rem;
    font-weight: 800;
    color: var(--text-dark);
    font-family: var(--font-secondary);
  }

  .stat-label {
    font-size: 0.85rem;
    color: var(--text-light);
    margin-top: 0.25rem;
  }
`;



const filters = [
  { id: 'all', label: 'Tous', bg: 'var(--primary-blue)', dotColor: 'var(--primary-blue)' },
  { id: 'active', label: 'Conflits actifs', bg: '#E74C3C', dotColor: '#E74C3C' },
  { id: 'mediation', label: 'En médiation', bg: '#F39C12', dotColor: '#F39C12' },
  { id: 'resolved', label: 'Résolus', bg: '#27AE60', dotColor: '#27AE60' },
];

const ConflictMap = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const { conflits, loading, error } = useConflits();

  // Utiliser uniquement les données Firebase
  const markers = conflits;

  const filteredMarkers = activeFilter === 'all'
    ? markers
    : markers.filter(m => m.status === activeFilter);

  // Calculer les statistiques dynamiques
  const stats = {
    active: markers.filter(m => m.status === 'active').length,
    mediation: markers.filter(m => m.status === 'mediation').length,
    resolved: markers.filter(m => m.status === 'resolved').length,
    regions: [...new Set(markers.map(m => m.label))].length
  };

  return (
    <Container>
      <Content>
        <SectionHeader>
          <Subtitle>Surveillance</Subtitle>
          <Title>Observatoire des conflits</Title>
          <Description>
            Carte interactive des zones de tension et actions de médiation à travers le territoire national
          </Description>
        </SectionHeader>
        
        <MapWrapper>
          <MapToolbar>
            <FilterGroup>
              {filters.map(filter => (
                <FilterButton
                  key={filter.id}
                  $active={activeFilter === filter.id}
                  $bg={filter.bg}
                  $dotColor={filter.dotColor}
                  onClick={() => setActiveFilter(filter.id)}
                >
                  <span className="dot" />
                  {filter.label}
                </FilterButton>
              ))}
            </FilterGroup>

            <ToolbarActions>
              <IconButton>
                <Filter size={18} />
                Filtres avancés
              </IconButton>
              <IconButton>
                <Download size={18} />
                Exporter
              </IconButton>
            </ToolbarActions>
          </MapToolbar>

          <MapContainer>
            <MapSVG viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid meet">
              {/* Fond de la carte */}
              <rect x="0" y="0" width="1000" height="800" fill="#f0f4f8" />
              
              {/* Contour du Tchad - forme réelle */}
              <MapRegionOutline d="M 120 180 L 150 80 L 280 20 L 450 15 L 650 30 L 850 80 L 920 180 L 950 300 L 920 450 L 880 600 L 800 720 L 650 750 L 450 760 L 280 750 L 150 700 L 80 550 L 50 400 L 60 280 Z" />
              
              {/* TIBESTI - Nord */}
              <MapRegion d="M 150 80 L 280 20 L 450 15 L 580 25 L 620 80 L 600 140 L 520 160 L 450 150 L 380 140 L 320 130 L 280 120 L 220 100 L 180 90 Z" />
              <RegionLabel x="380" y="90">Tibesti</RegionLabel>

              {/* BORKOU - Nord-Est */}
              <MapRegion d="M 450 15 L 650 30 L 750 60 L 780 120 L 750 180 L 680 200 L 600 180 L 550 160 L 520 160 L 600 140 L 620 80 L 580 25 Z" />
              <RegionLabel x="580" y="110">Borkou</RegionLabel>

              {/* ENNEDI - Extrême Nord-Est */}
              <MapRegion d="M 650 30 L 850 80 L 920 180 L 900 250 L 840 260 L 780 240 L 750 180 L 780 120 L 750 60 Z" />
              <RegionLabel x="800" y="150">Ennedi</RegionLabel>

              {/* WADI FIRI - Est */}
              <MapRegion d="M 780 240 L 840 260 L 900 250 L 940 320 L 920 400 L 860 420 L 800 400 L 760 350 L 750 300 L 760 260 Z" />
              <RegionLabel x="830" y="330">Wadi Fira</RegionLabel>

              {/* BATHA - Centre-Est */}
              <MapRegion d="M 600 180 L 680 200 L 750 180 L 780 240 L 760 260 L 750 300 L 760 350 L 740 400 L 680 420 L 620 400 L 580 360 L 560 300 L 570 240 L 590 200 Z" />
              <RegionLabel x="660" y="300">Batha</RegionLabel>

              {/* OUADDAÏ - Est-Sud */}
              <MapRegion d="M 760 350 L 800 400 L 860 420 L 920 400 L 900 480 L 850 520 L 780 510 L 740 480 L 720 430 L 740 400 Z" />
              <RegionLabel x="820" y="450">Ouaddaï</RegionLabel>

              {/* SILA - Sud-Est */}
              <MapRegion d="M 740 480 L 780 510 L 850 520 L 900 480 L 880 600 L 800 650 L 720 640 L 680 580 L 700 520 Z" />
              <RegionLabel x="800" y="580">Sila</RegionLabel>

              {/* SALAMAT - Centre-Sud-Est */}
              <MapRegion d="M 620 400 L 680 420 L 740 400 L 720 430 L 740 480 L 700 520 L 680 580 L 620 560 L 580 520 L 580 460 L 600 420 Z" />
              <RegionLabel x="650" y="500">Salamat</RegionLabel>

              {/* GUÉRA - Centre */}
              <MapRegion d="M 520 160 L 550 160 L 600 180 L 590 200 L 570 240 L 560 300 L 580 360 L 620 400 L 600 420 L 580 460 L 540 480 L 500 460 L 460 420 L 440 360 L 460 300 L 480 220 L 500 180 Z" />
              <RegionLabel x="520" y="320">Guéra</RegionLabel>

              {/* MOYEN-CHARI - Centre-Sud */}
              <MapRegion d="M 540 480 L 580 460 L 580 520 L 620 560 L 680 580 L 720 640 L 680 700 L 600 720 L 540 700 L 500 660 L 480 600 L 500 520 Z" />
              <RegionLabel x="580" y="620">Moyen-Chari</RegionLabel>

              {/* LOGONE ORIENTAL - Sud */}
              <MapRegion d="M 680 700 L 800 720 L 800 760 L 650 780 L 600 760 L 600 720 Z" />
              <RegionLabel x="700" y="740">Logone Oriental</RegionLabel>

              {/* LOGONE OCCIDENTAL - Sud-Ouest */}
              <MapRegion d="M 500 660 L 540 700 L 600 720 L 600 760 L 500 770 L 400 760 L 380 720 L 400 680 L 450 660 Z" />
              <RegionLabel x="500" y="730">Logone Occidental</RegionLabel>

              {/* MANDOUL - Sud (séparé de Moyen-Chari) */}
              <MapRegion d="M 600 720 L 650 750 L 600 760 Z" />
              
              {/* TANDJILÉ - Centre-Sud */}
              <MapRegion d="M 460 420 L 500 460 L 540 480 L 500 520 L 480 600 L 500 660 L 450 660 L 400 680 L 380 640 L 380 580 L 400 520 L 420 460 Z" />
              <RegionLabel x="450" y="560">Tandjilé</RegionLabel>

              {/* MAYO-KEBBI EST - Ouest-Sud */}
              <MapRegion d="M 380 580 L 380 640 L 400 680 L 380 720 L 320 740 L 260 720 L 240 680 L 260 620 L 300 580 L 340 560 Z" />
              <RegionLabel x="320" y="650">Mayo-Kebbi Est</RegionLabel>

              {/* MAYO-KEBBI OUEST - Extrême Ouest */}
              <MapRegion d="M 240 680 L 260 720 L 320 740 L 280 750 L 200 740 L 150 700 L 180 660 L 220 660 Z" />
              <RegionLabel x="220" y="700">Mayo-Kebbi Ouest</RegionLabel>

              {/* LAC - Nord-Ouest */}
              <MapRegion d="M 120 180 L 180 160 L 220 180 L 240 220 L 220 280 L 180 320 L 140 320 L 100 280 L 80 220 L 100 190 Z" />
              <RegionLabel x="160" y="250">Lac</RegionLabel>

              {/* HADJER-LAMIS - Centre-Ouest */}
              <MapRegion d="M 220 280 L 260 280 L 320 300 L 380 340 L 420 400 L 420 460 L 400 520 L 380 580 L 340 560 L 300 580 L 260 620 L 240 600 L 220 520 L 200 440 L 200 360 L 210 300 Z" />
              <RegionLabel x="300" y="450">Hadjer-Lamis</RegionLabel>

              {/* CHARI-BAGUIRMI - Centre (N'Djamena) */}
              <MapRegion $highlighted={true} d="M 180 320 L 220 280 L 260 280 L 320 300 L 380 340 L 420 400 L 400 440 L 360 460 L 320 480 L 280 500 L 240 520 L 200 520 L 180 480 L 170 420 L 160 360 Z" />
              <RegionLabel x="280" y="400">Chari-Baguirmi</RegionLabel>

              {/* BAHR EL GHAZEL - Nord-Centre */}
              <MapRegion d="M 180 160 L 220 140 L 280 120 L 320 130 L 380 140 L 450 150 L 520 160 L 500 180 L 480 220 L 460 300 L 440 360 L 420 400 L 380 340 L 320 300 L 260 280 L 220 280 L 240 220 L 220 180 Z" />
              <RegionLabel x="340" y="250">Barh El Gazel</RegionLabel>

              {/* KANEM - Nord-Ouest */}
              <MapRegion d="M 180 90 L 220 100 L 280 120 L 220 140 L 180 160 L 160 200 L 140 220 L 120 200 L 100 160 L 120 120 L 140 100 Z" />
              <RegionLabel x="180" y="160">Kanem</RegionLabel>

              {/* Ville de N'Djamena - marqueur spécial */}
              <circle cx="280" cy="420" r="10" fill="#002664" stroke="white" strokeWidth="3" />
              <text x="280" y="445" fontSize="11" fontWeight="800" fill="#002664" textAnchor="middle">N'Djamena</text>
              
              {/* Légende des frontières */}
              <text x="50" y="30" fontSize="12" fill="#666" fontWeight="600">Frontières nationales</text>
              <line x1="50" y1="40" x2="150" y2="40" stroke="#C8102E" strokeWidth="2" />
            </MapSVG>

            <MapMarkers>
              {filteredMarkers.map(marker => (
                <Marker key={marker.id} $x={marker.x} $y={marker.y} $color={marker.color}>
                  <div className="marker-dot" />
                  <div className="marker-tooltip">{marker.label}</div>
                </Marker>
              ))}
            </MapMarkers>

            {!loading && markers.length === 0 && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(255, 255, 255, 0.95)',
                padding: '2rem',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center',
                boxShadow: 'var(--shadow-lg)',
                maxWidth: '300px'
              }}>
                <Info size={48} color="var(--primary-blue)" style={{ marginBottom: '1rem' }} />
                <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: 'var(--text-dark)' }}>
                  Aucun conflit enregistré
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-medium)' }}>
                  Les données de l'observatoire seront bientôt disponibles.
                </p>
              </div>
            )}

            <MapLegend>
              <LegendTitle>
                <Info size={16} />
                Légende
              </LegendTitle>
              <LegendItem>
                <LegendDot $color="#E74C3C" />
                Conflit actif
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#F39C12" />
                En médiation
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#27AE60" />
                Résolu
              </LegendItem>
            </MapLegend>
          </MapContainer>

          <StatsBar>
            <StatItem>
              <div className="stat-value">{loading ? '-' : stats.active}</div>
              <div className="stat-label">Conflits actifs</div>
            </StatItem>
            <StatItem>
              <div className="stat-value">{loading ? '-' : stats.mediation}</div>
              <div className="stat-label">En médiation</div>
            </StatItem>
            <StatItem>
              <div className="stat-value">{loading ? '-' : stats.resolved}</div>
              <div className="stat-label">Résolus</div>
            </StatItem>
            <StatItem>
              <div className="stat-value">{loading ? '-' : stats.regions}</div>
              <div className="stat-label">Régions concernées</div>
            </StatItem>
          </StatsBar>
        </MapWrapper>
      </Content>
    </Container>
  );
};

export default ConflictMap;
